import { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import api from "@/lib/api";
import { ProductCard, ProductCardSkeleton } from "@/components/ProductCard";
import { FilterPanel } from "@/components/Filters";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import RichText from "@/components/RichText";

const CAT_TITLES = {
  "applications-mobiles": "Applications mobiles",
  "applications-web": "Applications web",
  "e-commerce": "E-commerce",
  "maintenance-support": "Maintenance & support",
};

// Cache des options de filtre — quasi-statique (change seulement quand des
// produits sont ajoutés/supprimés), pas besoin de refetch à chaque navigation.
let _filtersCache = null;

// Section "principale" du template Collection (v4, isolation par moteur de
// thème — voir /home/codespace/.claude/plans/curried-wondering-pumpkin.md) :
// contrairement aux autres sections du registre, celle-ci porte la grille de
// produits filtrable elle-même — déplacée ici telle quelle depuis
// frontend/src/pages/Catalog.jsx (devenu une simple coquille, voir ce
// fichier). Toujours présente par défaut (voir
// theme_engines/default/sections.py TEMPLATE_DEFAULT_ORDER["collection"]),
// jamais supprimable (voir Sidebar.jsx NO_DELETE_TYPES), seulement
// réordonnable/masquable comme n'importe quelle autre section.
export default function ProductGridSection({ props = {} }) {
  const t = props;
  const { categorySlug } = useParams();
  const [searchParams] = useSearchParams();
  const search = searchParams.get("search") || "";

  const [options, setOptions] = useState(_filtersCache);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("featured");
  const [filters, setFilters] = useState({ type: "", style: "", room: "" });
  const [priceRange, setPriceRange] = useState(
    _filtersCache ? [_filtersCache.price_min, _filtersCache.price_max] : [0, 500]
  );
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (_filtersCache) return;
    api.get("/filters").then(({ data }) => {
      _filtersCache = data;
      setOptions(data);
      setPriceRange([data.price_min, data.price_max]);
    });
  }, []);

  useEffect(() => {
    setFilters({ type: "", style: "", room: "" });
  }, [categorySlug]);

  useEffect(() => {
    if (!options) return;
    setLoading(true);
    const params = {
      sort,
      category: categorySlug || undefined,
      search: search || undefined,
      style: filters.style || undefined,
      room: filters.room || undefined,
      min_price: priceRange[0],
      max_price: priceRange[1],
    };
    api
      .get("/products", { params })
      .then(({ data }) => {
        const filtered = filters.type ? data.filter((p) => p.type === filters.type) : data;
        setProducts(filtered);
      })
      .finally(() => setLoading(false));
  }, [categorySlug, search, sort, filters, priceRange, options]);

  // props.heading (éditable dans le Studio) écrase le calcul dynamique pour
  // TOUTES les catégories s'il est renseigné — vide par défaut, pour ne
  // jamais remplacer silencieusement les titres par catégorie/recherche
  // déjà en place (voir DEFAULT_PROPS côté backend).
  const title = useMemo(() => {
    if (t.heading) return t.heading;
    if (search) return `Résultats pour « ${search} »`;
    return CAT_TITLES[categorySlug] || "Toutes nos offres";
  }, [t.heading, search, categorySlug]);

  const activeCount = Object.values(filters).filter(Boolean).length;

  return (
    <div className="px-6 md:px-12 pt-8 md:pt-12">
      <div className="mb-8">
        <RichText as="p" className="text-xs uppercase tracking-[0.2em] text-ash mb-3" html={t.eyebrow || "Nos offres"} />
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <RichText as="h1" className="font-serif text-4xl sm:text-5xl text-ink tracking-tight" html={title} />
          <div className="flex items-center gap-3">
            <button
              data-testid="open-filters-drawer"
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden flex items-center gap-2 border border-ink px-4 py-2.5 text-xs uppercase tracking-[0.15em]"
            >
              <SlidersHorizontal className="h-4 w-4" />
              Filtres {activeCount > 0 && `(${activeCount})`}
            </button>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="sort-select" className="w-[180px] rounded-sm border-stone bg-cream text-xs uppercase tracking-wider">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="featured">En vedette</SelectItem>
                <SelectItem value="price_asc">Prix croissant</SelectItem>
                <SelectItem value="price_desc">Prix décroissant</SelectItem>
                <SelectItem value="name">Nom (A-Z)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex gap-10">
        {/* Sidebar desktop */}
        <aside className="hidden lg:block w-64 shrink-0">
          <div className="sticky top-28">
            <FilterPanel
              options={options}
              filters={filters}
              setFilters={setFilters}
              priceRange={priceRange}
              setPriceRange={setPriceRange}
            />
          </div>
        </aside>

        {/* Grid */}
        <div className="flex-1">
          {!loading && products.length === 0 ? (
            <p data-testid="no-products" className="text-graphite py-20 text-center">
              {t.empty_message || "Aucune offre ne correspond à votre sélection."}
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-10 md:gap-x-7 md:gap-y-14">
              {loading
                ? Array.from({ length: 6 }).map((_, i) => <ProductCardSkeleton key={i} />)
                : products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}
        </div>
      </div>

      {/* Mobile filter drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-ink/40 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto bg-cream rounded-t-xl p-6 animate-fade-up">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl">Filtres</h2>
              <button data-testid="close-filters-drawer" onClick={() => setDrawerOpen(false)}>
                <X className="h-6 w-6" strokeWidth={1.5} />
              </button>
            </div>
            <FilterPanel
              options={options}
              filters={filters}
              setFilters={setFilters}
              priceRange={priceRange}
              setPriceRange={setPriceRange}
            />
            <button
              data-testid="apply-filters-btn"
              onClick={() => setDrawerOpen(false)}
              className="w-full bg-ink text-cream py-4 mt-4 uppercase tracking-[0.15em] text-xs"
            >
              Voir {products.length} résultats
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
